import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Homepage from './screens/Homepage';
import EmployeeSettingsPage from './screens/Admin/Settings';
import { AuthProvider } from './contexts/AuthContext';
import EmployeeOnlyRoute from './routes/EmployeeOnlyRoute';
import EmployeeAppointmentPage from './screens/EmployeeAppointmentPage';
import MyAppointmentsScreen from './screens/Admin/MyAppointmentsScreen';
import ServicesScreen from './screens/Admin/ServicesScreen';
import WebsiteDetailsPage from './screens/Admin/WebsiteDetailsPage';
import AddEmployeePage from './screens/Admin/AddEmployeePage';
import AdminDashboard from './screens/Admin/AdminDashboard/AdminDashboard';
import AdminLogin from './screens/Admin/AdminLogin';
import EditWorkHoursPage from './screens/Admin/EditWorkhours/EditWorkHoursPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import NotFoundPage from './screens/Admin/NotFound';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SignUpPage from './screens/Admin/SignUpPage';
import AdminOnlyRoute from './routes/AdminOnlyRoute';
import MyProfileScreen from './screens/Admin/MyProfilePage/MyProfilePage';
import TimezoneInitializer from './components/global/TimezoneInitializer';
import CustomerAppointmentPage from './screens/CustomerAppointmentPage';
import MyGalleryScreen from './screens/Admin/Gallery/MyGalleryScreen';
import SuperAdminOnlyRoute from './routes/SuperAdminOnlyRoute';
import ManageSitePage from './screens/SuperAdmin/ManageSitePage';
import SuperAdminDashboard from './screens/SuperAdmin/SuperAdminDashboard';
import EditBusinessConfigPage from './screens/SuperAdmin/BusinessConfigPage';
import PreviewSiteConfigPage from './screens/SuperAdmin/PreviewSiteConfig';
import SuperAdminAddEmployeePage from './screens/SuperAdmin/AddEmployee';
import SuperAdminEditServicesPage from './screens/SuperAdmin/EditServices';
import SuperAdminAnalyticsPage from './screens/SuperAdmin/SuperAdminAnalyticsPage';
import HomepageReviewsPage from './screens/SuperAdmin/HomepageReviewsPage';
import OwnerOnlyRoute from './routes/OwnerOnlyRoute';
import ManageSubscriptionPage from './screens/Admin/ManageSubscription/ManageSubscriptionPage';
import MissingSubscriptionPage from './screens/Admin/ManageSubscription/MissingSubscriptionPage';
import ResetPasswordPage from './screens/Admin/ResetPasswordPage';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentPortalPage from './screens/PaymentPortalPage';
import ScheduleHub from './screens/Admin/ScheduleHub';
import ContactSupportPage from './screens/Admin/ContactSupportPage';
import { AdminDataProvider } from './contexts/AdminDataContext';
import AnalyticsPage from './screens/Admin/Analytics/AnalyticsPage';
import CustomersPage from './screens/Admin/Customers/CustomersPage';
import ManageEmployeesPage from './screens/Admin/ManageEmployeesPage';

const ManifestController: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const manifestLink = document.getElementById('app-manifest');

    if (!(manifestLink instanceof HTMLLinkElement)) {
      return;
    }

    const manifestHref = location.pathname.startsWith('/admin')
      ? `${process.env.PUBLIC_URL}/admin.webmanifest`
      : `${process.env.PUBLIC_URL}/manifest.json`;

    if (manifestLink.href !== new URL(manifestHref, window.location.origin).href) {
      manifestLink.href = manifestHref;
    }
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  const queryClientRef = useRef<QueryClient>();
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: { queries: { staleTime: 1000 * 60 * 20 } },
    });
    queryClientRef.current.setQueryDefaults(['currentEmployee'], {
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
    queryClientRef.current.setQueryDefaults(['appointment'], {
      gcTime: Infinity, // never evict unless you explicitly remove it
    });
  }
  const queryClient = queryClientRef.current;

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);


  return (
    <Elements stripe={stripePromise}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <TimezoneInitializer />
            <Router>
              <ManifestController />
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Homepage />} />
                <Route path="/book-now" element={<Homepage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Admin entry + protected routes */}
                <Route path="admin">
                  <Route index element={<AdminLogin />} />
                  <Route

                    element={
                      <EmployeeOnlyRoute>
                        <AdminDataProvider>
                          <AdminDashboard />
                        </AdminDataProvider>
                      </EmployeeOnlyRoute>
                    }
                  >
                    {/* default dashboard view */}
                    <Route path="dashboard" element={<ScheduleHub />} />
                    <Route index element={<ScheduleHub />} />

                    {/* Only admins can access these routes */}
                    <Route path="manage-services" element={<AdminOnlyRoute element={<ServicesScreen />} />} />
                    <Route path="website-details" element={<AdminOnlyRoute element={<WebsiteDetailsPage />} />} />
                    <Route path="add-employee" element={<AdminOnlyRoute element={<AddEmployeePage />} />} />
                    {/* <Route path="business-config" element={<AdminOnlyRoute element={<BusinessConfigForm />} />} /> */}
                    <Route path="gallery" element={<AdminOnlyRoute element={<MyGalleryScreen />} />} />

                    {/* SuperAdmin Routes */}
                    <Route path="superadmin" element={<SuperAdminOnlyRoute element={<SuperAdminDashboard />} />} />
                    <Route path="superadmin/manage/:siteId" element={<SuperAdminOnlyRoute element={<ManageSitePage />} />} />
                    <Route path="superadmin/preview-site/:code" element={<SuperAdminOnlyRoute element={<PreviewSiteConfigPage />} />} />
                    <Route path="superadmin/analytics" element={<SuperAdminOnlyRoute element={<SuperAdminAnalyticsPage />} />} />
                    <Route
                      path="superadmin/manage/:siteId/businessConfig"
                      element={<SuperAdminOnlyRoute element={<EditBusinessConfigPage />} />}
                    />
                    <Route path="superadmin/manage/:siteId/add-employee" element={<SuperAdminOnlyRoute element={<SuperAdminAddEmployeePage />} />} />
                    <Route path="superadmin/manage/:siteId/manage-services" element={<SuperAdminOnlyRoute element={<SuperAdminEditServicesPage />} />} />
                    <Route path="superadmin/manage/:siteId/homepage-reviews" element={<SuperAdminOnlyRoute element={<HomepageReviewsPage />} />} />

                    {/* Employee routes */}
                    <Route path="edit-work-hours" element={<EditWorkHoursPage />} />
                    {/* <Route path="manage-schedule" element={<ManageSchedule />} /> */}
                    <Route path="appointments" element={<MyAppointmentsScreen />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="settings" element={<EmployeeSettingsPage />} />
                    <Route path="contact-support" element={<ContactSupportPage />} />
                    <Route path="payment-portal" element={<PaymentPortalPage />} />
                    <Route path="manage-employees" element={<AdminOnlyRoute element={<ManageEmployeesPage />} />} />

                    <Route path="my-profile" element={<MyProfileScreen />} />
                    {/* <Route path="my-calendar" element={<CalendarScreen />} /> */}
                    <Route path="schedule-hub" element={<ScheduleHub />} />
                    <Route path="missing-subscription" element={<MissingSubscriptionPage />} />

                    {/* Owner Routes */}
                    <Route path="manage-subscription" element={<OwnerOnlyRoute element={<ManageSubscriptionPage />} />} />
                    <Route path=":appointmentId" element={<EmployeeAppointmentPage />} />
                  </Route>
                </Route>


                {/* Signup & Catch-All */}
                <Route path="MyAppointment" element={<CustomerAppointmentPage />} />
                <Route path="SignUp" element={<SignUpPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes >
            </Router >
            <ReactQueryDevtools />
          </QueryClientProvider>

        </AuthProvider >
      </LocalizationProvider >
    </Elements >
  );
};

export default App;
